import React, { useState, useEffect, useRef } from 'react';
import { Button as ANTButton, Form, Switch, notification } from 'antd';
import Select from 'components/Atom/Select';
import Input from 'components/Atom/Input';
import { Button } from 'components/Atom/Button';
import { useSelector } from 'react-redux';
import { createXPayUser, updateXPayUser, addXPayUserRole, deleteXPayUserRole } from '../../utils/copilot';

const CreateUser = ({ handleOpen, refetch, userData, isEdit, userList = [] }) => {
  const [isUserAdding, setIsUserAdding] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const store = useSelector((state) => state?.company?.store);
  const user_id = store?.user_id;
  const [form] = Form?.useForm();
  const initialRef = useRef(null);

  const USER_ROLE_OPTIONS = [
    { label: 'READ ONLY', value: 'xpay_user' },
    { label: 'ADMIN', value: 'xpay' },
    { label: 'USER MANAGEMENT', value: 'xstak-copilot-admin' },
    { label: 'REFUND', value: 'xpay_refund' },
    { label: 'DEVELOPER', value: 'xpay_developer' },
    { label: 'RESTRICTED ADMIN', value: 'xpay_restricted_admin' },
    { label: 'SUBSCRIPTION USER', value: 'xpay_subscription_user' },
    { label: 'SUBSCRIPTION MODULE ONLY', value: 'xpay_subscription_module_only' },
  ];

  const buildInitialValues = () => {
    if (!isEdit) return undefined;
    const roles = userData?.roles || [];
    let roleValue = 'xpay';
    let roleLabel = 'ADMIN';
    const nonXpayRole = roles?.find((r) => r !== 'xpay');
    if (nonXpayRole) {
      roleValue = nonXpayRole;
      const found = USER_ROLE_OPTIONS?.find((r) => r?.value === nonXpayRole);
      roleLabel = found ? found?.label : 'ADMIN';
    }
    return {
      firstName: userData?.firstName ?? '',
      lastName: userData?.lastName ?? '',
      email: userData?.email ?? '',
      role: { label: roleLabel, value: roleValue },
      status: !!userData?.enabled,
    };
  };

  useEffect(() => {
    const init = buildInitialValues();
    if (init) {
      form?.setFieldsValue(init);
      initialRef.current = {
        firstName: init?.firstName ?? '',
        lastName: init?.lastName ?? '',
        roleValue: init?.role?.value ?? 'xpay',
        status: !!init?.status,
      };
    } else {
      initialRef.current = null;
      form?.resetFields();
    }
    setHasChanges(false);
  }, [userData, isEdit]);

  const handleValuesChange = (_, allValues) => {
    if (!isEdit || !initialRef?.current) {
      setHasChanges(true);
      return;
    }
    const current = {
      firstName: allValues?.firstName ?? '',
      lastName: allValues?.lastName ?? '',
      roleValue: allValues?.role?.value ?? 'xpay',
      status: !!allValues?.status,
    };

    const initial = initialRef?.current;

    const firstNameChanged = current?.firstName !== (initial?.firstName ?? '');
    const lastNameChanged = current?.lastName !== (initial?.lastName ?? '');
    const roleChanged = current?.roleValue !== (initial?.roleValue ?? 'xpay');
    const statusChanged = current?.status !== (initial?.status ?? false);

    setHasChanges(firstNameChanged || lastNameChanged || roleChanged || statusChanged);
  };

  const onAddSubmit = async (values) => {
    setIsUserAdding(true);
    const { firstName, lastName, email, role, status } = values;
    const roleVal = role?.value ?? 'xpay';
    let userRoles = roleVal === 'xpay' ? ['xpay'] : ['xpay', roleVal];

    if (!isEdit) {
      const emailExists = userList?.some((user) => user?.email?.toLowerCase() === email?.toLowerCase());

      if (emailExists) {
        notification.error({
          message: 'Duplicate Email',
          description: 'This email is already associated with another user.',
          placement: 'bottomRight',
        });
        setIsUserAdding(false);
        return;
      }
      const payload = {
        realm: user_id,
        firstName,
        lastName,
        username: email,
        email,
        userRoles,
        clientType: 'xPay',
      };
      try {
        const res = await createXPayUser(payload);
        if (res?.status === 200 || res?.status === 201) {
          notification.success({
            message: 'User Created',
            description: 'User has been created successfully.',
            placement: 'bottomRight',
          });
          handleOpen();
          refetch();
        } else {
          notification.error({
            message: 'User cannot be created. Please check the required fields and try again.',
            placement: 'bottomRight',
          });
        }
      } catch (error) {
        notification.error({
          message: error?.response?.message || error?.response?.data || 'Something went wrong while creating user.',
          placement: 'bottomRight',
        });
      } finally {
        setIsUserAdding(false);
      }
      return;
    }

    try {
      const initial = initialRef?.current || {};
      const firstNameChanged = (firstName ?? '') !== (initial?.firstName ?? '');
      const lastNameChanged = (lastName ?? '') !== (initial?.lastName ?? '');
      const statusChanged = !!status !== !!initial?.status;
      const roleChanged = (role?.value ?? 'xpay') !== (initial?.roleValue ?? 'xpay');

      let statusRes = null;
      let roleRes = null;

      if (firstNameChanged || lastNameChanged || statusChanged) {
        const payload = {
          realm: user_id,
          email,
          firstName,
          lastName,
          status,
        };
        statusRes = await updateXPayUser(payload);
      }

      if (roleChanged) {
        const deleteUserRole = {
          realm: user_id,
          email,
          roles: userData?.roles,
        };

        const addUserRole = {
          realm: user_id,
          username: email,
          email,
          firstName,
          lastName,
          userRoles,
        };

        try {
          const deleteRes = await deleteXPayUserRole(deleteUserRole);
          if (deleteRes?.status === 200 || deleteRes?.status === 201) {
            const addRes = await addXPayUserRole(addUserRole);
            if (addRes?.status === 200 || addRes?.status === 201) {
              roleRes = addRes;
            } else {
              throw new Error('Failed to add new user roles.');
            }
          } else {
            throw new Error('Failed to delete existing user roles.');
          }
        } catch (err) {
          console.error('Role update failed:', err);
          notification.error({
            message: 'Role update failed',
            description: err?.response?.data || err.message || 'Error updating user roles.',
            placement: 'bottomRight',
          });
        }
      }

      if (statusRes || roleRes) {
        notification.success({
          message: 'User Updated',
          description: 'User details updated successfully.',
          placement: 'bottomRight',
        });
        handleOpen();
        refetch();
      } else {
        notification.info({
          message: 'No changes detected',
          description: 'Nothing was updated since no changes were made.',
          placement: 'bottomRight',
        });
      }
    } catch (error) {
      notification.error({
        message: error?.response?.message || error?.response?.data || 'Something went wrong while updating user.',
        placement: 'bottomRight',
      });
    } finally {
      setIsUserAdding(false);
    }
  };

  return (
    <div className='formDetails'>
      <Form layout='vertical' form={form} onFinish={onAddSubmit} initialValues={buildInitialValues()} onValuesChange={handleValuesChange}>
        <div className='create-form' style={{ gridTemplateColumns: '1fr', marginBottom: '24px' }}>
          <Form.Item
            name='firstName'
            label='First Name'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={[
              !isEdit && { required: true, message: 'First Name is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[^%?{}[\]$^@<>\"&!:;~*]+$/;
                  if (regex?.test(value)) return Promise.resolve();
                  return Promise.reject('%?{}[]$^@<>"&!:;~* are not allowed.');
                },
              },
            ].filter(Boolean)}>
            <Input type='string' placeholder='Enter first name' />
          </Form.Item>
          <Form.Item
            name='lastName'
            label='Last Name'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={[
              !isEdit && { required: true, message: 'Last Name is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[^%?{}[\]$^@<>\"&!:;~*]+$/;
                  if (regex.test(value)) return Promise.resolve();
                  return Promise.reject('%?{}[]$^@<>"&!:;~* are not allowed.');
                },
              },
            ]?.filter(Boolean)}>
            <Input type='string' placeholder='Enter last name' />
          </Form.Item>
          <Form.Item
            name='email'
            label='Email'
            className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`}
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}>
            <Input disabled={isEdit} placeholder='Enter email' />
          </Form.Item>

          <Form.Item name='role' label='Role' className={`customInput mobileInputView ${isEdit ? '' : 'steric-after'}`} rules={[{ required: true, message: 'Role is required' }]}>
            <Select placeholder='Select Role' options={USER_ROLE_OPTIONS} />
          </Form.Item>
          {isEdit && (
            <Form.Item name='status' label='Active User' valuePropName='checked' className='customInput mobileInputView'>
              <Switch />
            </Form.Item>
          )}
        </div>
        <div className='mb-[-20px] form_button update-button'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <ANTButton block className='rounded-lg' onClick={() => handleOpen()}>
                Cancel
              </ANTButton>
              <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isUserAdding} disabled={isEdit ? !hasChanges : false}>
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CreateUser;
